import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inr } from "@/lib/calc";
import { DECISION } from "@/components/SpotDecision";
import { spotExceptions, exportSpotExceptionsXlsx, localDay } from "@/lib/export";

const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN") : "—");

const BASES = [{ id: "receipt", label: "Receipt date", day: (r) => localDay(r.created_at) }, { id: "payment", label: "Payment date", day: (r) => r.payment.date }];

export const SpotExceptions = ({ all, from, to, fileTag }) => {
  const nav = useNavigate();
  const [basis, setBasis] = useState("receipt");
  const b = BASES.find((x) => x.id === basis);
  const list = spotExceptions(all).filter((r) => { const d = b.day(r); return d >= from && d <= to; });
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-5" data-testid="spot-exceptions">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-plum-950">
          <AlertTriangle className="h-4 w-4 text-amber-600" />Spot exceptions
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-num text-xs text-amber-900" data-testid="spot-exceptions-count">{list.length}</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-plum-900/15 p-0.5 text-xs" data-testid="spot-exceptions-basis">
            {BASES.map((x) => (
              <button key={x.id} onClick={() => setBasis(x.id)} data-testid={`spot-basis-${x.id}`}
                className={`rounded px-2.5 py-1 font-medium transition-colors duration-150 ${basis === x.id ? "bg-plum-900 text-white" : "text-slate-600 hover:bg-plum-900/5"}`}>{x.label}</button>
            ))}
          </div>
          <Button size="sm" variant="outline" disabled={!list.length} onClick={() => exportSpotExceptionsXlsx(list, `Spot_Exceptions_${basis}_${fileTag}.xlsx`)} data-testid="spot-exceptions-export-btn">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel
          </Button>
        </div>
      </div>
      {!list.length ? (
        <p className="text-sm text-slate-500" data-testid="spot-exceptions-empty">No admitted Spot receipts broke the rule in this period.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {list.map((r) => (
            <button key={r.id} onClick={() => nav(`/receipts/${r.id}`)} data-testid={`spot-exception-${r.receipt_no}`}
              className="grid w-full gap-1 py-2.5 text-left text-sm transition-colors duration-150 hover:bg-slate-50 md:grid-cols-[1.4fr_1fr_2fr_auto] md:items-center md:gap-4">
              <span><span className="font-num mr-2 text-slate-500">{r.receipt_no}</span><span className="font-medium">{r.inputs.childName}</span>
                <span className="ml-2 text-xs text-slate-500">{r.inputs.grade} · {r.inputs.counselorName}</span></span>
              <span className="text-xs text-slate-600">Enquiry {fmt(r.inputs.enquiryDate)} · Paid {fmt(r.payment.date)}</span>
              <span className="text-xs font-medium text-amber-800">{r.payment.spot_issues.join("; ")}
                <span className="mt-0.5 block font-normal text-slate-600" data-testid={`spot-exception-reason-${r.receipt_no}`}>Reason: {r.payment.spot_note || "—"}</span>
                <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.payment.spot_decision ? DECISION[r.payment.spot_decision.decision].cls : "bg-slate-100 text-slate-600"}`} data-testid={`spot-exception-decision-${r.receipt_no}`}>
                  {r.payment.spot_decision ? r.payment.spot_decision.decision : "pending review"}
                </span></span>
              <span className="font-num text-xs text-slate-600">Disc. ₹ {inr(r.computed.totalDiscount)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
